import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useDataRefresh, DEFAULT_POLL_INTERVAL_MS } from '@/hooks/useDataRefresh';
import { Plus, Search, Thermometer, Timer, Gauge, FileText, Zap, Clock, TrendingUp } from 'lucide-react';
import { ModuleTabs } from '@/components/ui/ModuleTabs';
import { RecipeFormDialog } from '@/components/Recipes/RecipeFormDialog';
import type { ProductionRecipe } from '@/types/legacy';

interface RecipeRamp {
  id: string;
  recipe_id: string;
  ramp_order: number;
  target_temp: number | null;
  rate_deg_min: number | null;
  hold_minutes: number | null;
  pressure_bar: number | null;
}

interface RecipeWithRamps extends ProductionRecipe {
  ramps?: RecipeRamp[];
}

export default function ProductionRecipes() {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [recipes, setRecipes] = useState<RecipeWithRamps[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState<ProductionRecipe | null>(null);

  const fetchRecipes = async () => {
    try {
      const { data, error } = await api
        .from('production_recipes')
        .select('*, ramps:production_recipe_ramps(*)')
        .order('name');

      if (error) throw error;
      // Ordenar rampas por ramp_order
      const withSortedRamps = (data || []).map((r: any) => ({
        ...r,
        ramps: (r.ramps || []).sort((a: RecipeRamp, b: RecipeRamp) => a.ramp_order - b.ramp_order),
      }));
      setRecipes(withSortedRamps);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las recetas',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipes();
  }, []);
  useDataRefresh(fetchRecipes, { pollIntervalMs: DEFAULT_POLL_INTERVAL_MS });

  const filteredRecipes = recipes.filter(recipe =>
    recipe.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    recipe.notes?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (recipe: any) => {
    if (!window.confirm(`¿Eliminar la receta "${recipe.name}"? Se marcará como inactivo y se conserva para trazabilidad.`)) return;
    const { error } = await api.from('production_recipes').update({ is_active: false, updated_at: new Date().toISOString() }).eq('id', recipe.id);
    if (error) { toast({ title: 'Error', description: 'No se pudo eliminar: ' + error.message, variant: 'destructive' }); return; }
    toast({ title: 'Eliminado', description: 'Marcado como inactivo (histórico conservado).' });
    fetchRecipes();
  };

  const handleEdit = (recipe: ProductionRecipe) => {
    setSelectedRecipe(recipe);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedRecipe(null);
    setDialogOpen(true);
  };

  const handleDialogChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) fetchRecipes();
  };

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Module Tabs */}
        <ModuleTabs
          tabs={[
            { value: 'general', label: 'Recetas Generales', href: '/recipes' },
            { value: 'curado', label: 'Recetas Curado', href: '/curing-recipes' },
          ]}
        />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Recetas de Produccion
            </h1>
            <p className="text-muted-foreground mt-2">
              Formulas y rampas de curado
            </p>
          </div>
          {canManage && (
            <Button onClick={handleNew} className="bg-gradient-magma hover:shadow-magma-hover">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Receta
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Buscar recetas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Recetas</p>
                <p className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                  {recipes.length}
                </p>
              </div>
              <Timer className="w-8 h-8 text-primary/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Activas</p>
                <p className="text-3xl font-bold text-success">
                  {recipes.filter(r => r.is_active).length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Inactivas</p>
                <p className="text-3xl font-bold text-muted-foreground">
                  {recipes.filter(r => !r.is_active).length}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Recipes Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredRecipes.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <Timer className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              {searchTerm ? 'No se encontraron recetas' : 'No hay recetas registradas'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRecipes.map((recipe) => (
              <Card key={recipe.id} className="glass-card p-6 hover:shadow-magma transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">{recipe.name}</h3>
                    {recipe.ramps && recipe.ramps.length > 0 && (
                      <Badge variant="secondary" className="gap-1">
                        <TrendingUp className="w-3 h-3" />
                        {recipe.ramps.length} rampa{recipe.ramps.length !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                  <Badge variant={recipe.is_active ? 'default' : 'secondary'}>
                    {recipe.is_active ? 'Activa' : 'Inactiva'}
                  </Badge>
                </div>

                {/* Rampas */}
                {recipe.ramps && recipe.ramps.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {recipe.ramps.map((ramp, idx) => (
                      <div key={ramp.id || idx} className="rounded-lg border bg-muted/30 p-2.5">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-bold">
                            {idx + 1}
                          </span>
                          <span className="text-xs font-medium text-muted-foreground">Rampa {idx + 1}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                          {ramp.target_temp != null && (
                            <div className="flex items-center gap-1">
                              <Thermometer className="w-3 h-3 text-orange-500" />
                              <span className="font-medium">{ramp.target_temp}C</span>
                            </div>
                          )}
                          {ramp.rate_deg_min != null && (
                            <div className="flex items-center gap-1">
                              <Zap className="w-3 h-3 text-yellow-500" />
                              <span className="font-medium">{ramp.rate_deg_min}C/min</span>
                            </div>
                          )}
                          {ramp.hold_minutes != null && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3 text-purple-500" />
                              <span className="font-medium">{ramp.hold_minutes} min</span>
                            </div>
                          )}
                          {ramp.pressure_bar != null && (
                            <div className="flex items-center gap-1">
                              <Gauge className="w-3 h-3 text-blue-500" />
                              <span className="font-medium">{ramp.pressure_bar} bar</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mb-4 italic">Sin rampas definidas</p>
                )}

                {recipe.notes && (
                  <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      Notas:
                    </p>
                    <p className="text-sm line-clamp-2">{recipe.notes}</p>
                  </div>
                )}

                {canManage && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => handleEdit(recipe)}
                    >
                      Editar
                    </Button>
                    {recipe.is_active && <Button variant="outline" size="sm" className="w-full mt-2 text-destructive" onClick={() => handleDelete(recipe)}>Eliminar</Button>}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      <RecipeFormDialog
        open={dialogOpen}
        onOpenChange={handleDialogChange}
        recipe={selectedRecipe}
      />
    </DashboardLayout>
  );
}
